import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import { buildBounds } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-bounds';
import { formatMonthDayInTimezone } from '@/utils/date';
import { getCurrencySymbol } from '@/utils/money';
import { percentSectionId } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-range-modes';
import {
  PillFilterSection,
  PricingFilterSection,
  PricingFilterSectionKind,
  PricingFilterUnit,
  PricingRangeColumnConfig,
  RangeFilterSection,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { PriceEditDisplayCell } from '@sales-portal/blocs/utils/price-edit-display';
import { WeeklyPurchaseQuantity } from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';

// Column-backed sections, shared by every screen's builder — unlike the
// special sections, these carry their own column's id so hiding a column
// leaves the filter in place.
export const buildUOMSection = <T extends { variantPackUOM: string }>(
  columnId: string,
  rows: T[],
): PillFilterSection<T> => {
  const uoms = Array.from(new Set(rows.map((row) => row.variantPackUOM))).sort(
    (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );

  return {
    getValues: (row) => [row.variantPackUOM],
    id: columnId,
    kind: PricingFilterSectionKind.Pill,
    options: uoms.map((uom) => ({ label: uom, value: uom })),
    singleSelect: false,
    title: i18n.t('pricing.table.header.uom'),
  };
};

export const buildWeeklyPurchaseQuantitiesSection = <
  T extends { weeklyPurchaseQuantities: WeeklyPurchaseQuantity[] },
>(
  columnId: string,
  rows: T[],
  timezone: string,
): PillFilterSection<T> => {
  const orderedWeeks = (row: T): string[] =>
    row.weeklyPurchaseQuantities
      .filter((week) => week.quantity > 0)
      .map((week) => week.startDate);

  const startDates = Array.from(new Set(rows.flatMap(orderedWeeks))).sort();

  return {
    getValues: orderedWeeks,
    id: columnId,
    kind: PricingFilterSectionKind.Pill,
    options: startDates.map((startDate) => ({
      label: i18n.t('pricing.filters.week_of', {
        date: formatMonthDayInTimezone(
          moment.tz(startDate, timezone),
          timezone,
        ),
      }),
      value: startDate,
    })),
    singleSelect: false,
    title: i18n.t('pricing.table.header.weekly_purchase_quantities'),
  };
};

interface DisplayInfoRow {
  supplierDisplayInfo: Record<string, PriceEditDisplayCell>;
}

const displayCell = <T extends DisplayInfoRow>(
  row: T,
  columnId: string,
): PriceEditDisplayCell | undefined => row.supplierDisplayInfo?.[columnId];

// The server-driven columns (`price_edit_display_headers`) carry no type, so
// the kind is inferred from the rows: all-numeric becomes a range, anything
// else a pill over the formatted values. `title` is the already-localized
// header label, since these have no i18n key of their own.
export const buildDisplayInfoSection = <T extends DisplayInfoRow>(
  columnId: string,
  title: string,
  rows: T[],
): PricingFilterSection<T> => {
  const cells = rows
    .map((row) => displayCell(row, columnId))
    .filter((cell): cell is PriceEditDisplayCell => Boolean(cell));

  const present = cells.filter(
    (cell) => cell.raw !== null && cell.raw !== undefined,
  );
  const numeric =
    present.length > 0 && present.every((cell) => typeof cell.raw === 'number');

  if (!numeric) {
    const values = Array.from(
      new Set(
        cells
          .map((cell) => cell.display)
          .filter((display): display is string => Boolean(display)),
      ),
    ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

    // value must stay comma-free since the URL joins/splits selections on ','
    return {
      getValues: (row) => {
        const display = displayCell(row, columnId)?.display;
        return display ? [encodeURIComponent(display)] : [];
      },
      id: columnId,
      kind: PricingFilterSectionKind.Pill,
      options: values.map((value) => ({
        label: value,
        value: encodeURIComponent(value),
      })),
      singleSelect: false,
      title,
    };
  }

  const isPercent = present.some((cell) => cell.isPercent);
  const currency = present.find((cell) => cell.currency)?.currency;
  const getValue = (row: T): number | null => {
    const raw = displayCell(row, columnId)?.raw;
    return typeof raw === 'number' ? raw : null;
  };

  return {
    affix: isPercent ? '%' : currency ? getCurrencySymbol(currency) : '',
    bounds: buildBounds(rows, getValue),
    getValue,
    id: columnId,
    kind: PricingFilterSectionKind.Range,
    title,
    unit: isPercent
      ? PricingFilterUnit.Percent
      : currency
        ? PricingFilterUnit.Currency
        : PricingFilterUnit.Unit,
  };
};

// A `filterPillMetric` yields a $/% pair sharing a title and `toggleGroupId`,
// which the sidebar renders as one header with a toggle.
export const buildRangeSections = <T>(
  columnId: string,
  config: PricingRangeColumnConfig<T>,
  rows: T[],
): RangeFilterSection<T>[] => {
  const currency: RangeFilterSection<T> = {
    affix: config.affix,
    bounds: buildBounds(rows, config.accessor),
    getValue: config.accessor,
    id: columnId,
    kind: PricingFilterSectionKind.Range,
    title: config.title,
    unit: config.unit,
  };

  const filterPillMetric = config.filterPillMetric;
  if (!filterPillMetric) {
    return [currency];
  }

  return [
    { ...currency, toggleGroupId: columnId },
    {
      affix: '%',
      bounds: buildBounds(rows, filterPillMetric),
      getValue: filterPillMetric,
      id: percentSectionId(columnId),
      kind: PricingFilterSectionKind.Range,
      title: config.title,
      toggleGroupId: columnId,
      unit: PricingFilterUnit.Percent,
    },
  ];
};
